import React, { useState } from 'react';
import { downloadBidderReportPdf } from 'components/molecules/bidding_summary_bidder_report_pdf';
import AiManagementItemCard from 'components/molecules/ai_management_item_card';
import AiManagementItemsSearch from 'components/molecules/ai_management_items_search';
import AiManagementItemsSection from 'components/molecules/ai_management_items_section';
import AiManagementItemsToolbar from 'components/molecules/ai_management_items_toolbar';
import AiManagementHiringHistoryBiddings from 'components/molecules/ai_management_hiring_history_biddings';
import { HiringHistoryItem, useHiringHistory } from 'hooks/useHiringHistory';
import { useItemSelection } from 'hooks/useItemSelection';
import { useBidderFileGeneration } from 'hooks/useBidderFileGeneration';
import { BidderFileGeneratedHandler } from 'services/actions/ai_management_files';
import { listHiringHistoryBiddings } from 'services/actions/ai_management_hiring_history';
import { money, plural, roundedMoney } from 'services/ai_management_format';
import { buildHiringHistoryReport } from 'services/ai_management_hiring_history_report';

interface AiManagementHiringHistoryProps {
  biddingId: number | null;
  publicBodyName: string | null;
  noticeNumber: string | null;
  onFileGenerated: BidderFileGeneratedHandler;
  onOpenBidderFiles: () => void;
}

const itemMeta = (item: HiringHistoryItem) =>
  [
    item.biddings > 0 ? plural(item.biddings, 'licitação', 'licitações') : null,
    item.averageValue !== null
      ? `${roundedMoney(item.averageValue)} de valor médio contratado`
      : null,
  ].filter((text): text is string => Boolean(text));

const itemTitle = (item: HiringHistoryItem) =>
  [
    `${plural(item.lines, 'item contratado', 'itens contratados')} em ${plural(
      item.biddings,
      'licitação',
      'licitações',
    )}`,
    `mediana ${money(item.medianValue)} por item`,
    `total contratado ${money(item.totalSpent)}`,
  ].join(' · ');

const AiManagementHiringHistory: React.FC<AiManagementHiringHistoryProps> = ({
  biddingId,
  publicBodyName,
  noticeNumber,
  onFileGenerated,
  onOpenBidderFiles,
}) => {
  const history = useHiringHistory(biddingId);
  const { items, total, page, perPage, loading, error } = history;
  const selection = useItemSelection(items);
  const file = useBidderFileGeneration('historico_contratacao', onFileGenerated);
  const [expanded, setExpanded] = useState<string | null>(null);
  const orgao = history.publicBodyName ?? publicBodyName;

  const handleGenerate = () =>
    file.generate(async () => {
      if (!biddingId) return null;
      const chosen = selection.selectedItems;
      const responses = await Promise.all(
        chosen.map((item) => listHiringHistoryBiddings(biddingId, item.key)),
      );
      const number = noticeNumber?.trim() || String(biddingId);
      return downloadBidderReportPdf(
        buildHiringHistoryReport({
          items: chosen,
          biddingsByKey: Object.fromEntries(
            chosen.map((item, index) => [item.key, responses[index].data.items]),
          ),
          biddingNumber: number,
          publicBody: orgao ?? '',
          conlicitacaoId: String(biddingId),
        }),
        `Histórico de Contratações ${number} - Conlicitação ${biddingId}`,
      );
    });

  const emptyState = () => {
    if (items.length) return null;
    if (history.appliedSearch) {
      return {
        title: 'Nenhum item encontrado',
        text: `Não encontramos itens com "${history.appliedSearch}" no histórico deste órgão.`,
      };
    }
    return {
      title: 'Sem histórico de contratações para este órgão',
      text: 'O histórico é montado a partir dos resultados de licitações publicados pelos portais. Este órgão ainda não tem nenhum resultado disponível.',
    };
  };

  return (
    <AiManagementItemsSection
      title="Histórico de Contratações"
      subtitle={orgao ? `Referentes ao órgão ${orgao}` : 'Referentes ao órgão desta licitação'}
      toolbar={
        <AiManagementItemsToolbar
          id="hiring-history"
          shown={items.length}
          total={total}
          showActions={!loading && !error && items.length > 0}
          allSelected={selection.allSelected}
          selectedCount={selection.selectedKeys.length}
          allSelectedInterested={selection.allSelectedInterested}
          generating={file.generating}
          showAccessFile={file.showAccessFile}
          onToggleAll={selection.toggleAll}
          onToggleInterest={() =>
            history.toggleInterest(selection.selectedKeys, !selection.allSelectedInterested)
          }
          onGenerate={handleGenerate}
          onOpenBidderFiles={onOpenBidderFiles}
        />
      }
      search={
        <AiManagementItemsSearch
          value={history.search}
          showClear={Boolean(history.search || history.appliedSearch)}
          onChange={history.setSearch}
          onSubmit={history.submitSearch}
          onClear={history.clearSearch}
        />
      }
      loading={loading}
      errorMessage={
        error ? 'Não foi possível carregar o histórico de contratações deste órgão.' : null
      }
      empty={emptyState()}
      page={page}
      perPage={perPage}
      total={total}
      onPageChange={history.setPage}
    >
      {items.map((item) => (
        <AiManagementItemCard
          key={item.key}
          id="hiring-history-item"
          label={item.label}
          meta={itemMeta(item)}
          title={itemTitle(item)}
          interested={item.interested}
          selected={selection.isSelected(item.key)}
          expanded={expanded === item.key}
          onToggleSelected={() => selection.toggle(item)}
          onToggleExpanded={() => setExpanded(expanded === item.key ? null : item.key)}
          onToggleInterest={() => history.toggleInterest([item.key], !item.interested)}
        >
          <AiManagementHiringHistoryBiddings biddingId={biddingId} itemKey={item.key} />
        </AiManagementItemCard>
      ))}
    </AiManagementItemsSection>
  );
};

export default AiManagementHiringHistory;
